export interface SseEvent {
  event: string;
  data: string;
  id: string;
}

export interface SseParserOptions {
  onEvent: (event: SseEvent) => void;
  onComment?: (comment: string) => void;
  onRetry?: (ms: number) => void;
}

export interface SseParser {
  push: (chunk: string) => void;
  flush: () => void;
  reset: () => void;
}

export function createSseParser(options: SseParserOptions): SseParser {
  let buffer = "";
  let skipLeadingLf = false;
  let eventName = "";
  let dataLines: string[] = [];
  let hasData = false;
  let lastEventId = "";

  const dispatch = () => {
    if (hasData) {
      options.onEvent({
        event: eventName || "message",
        data: dataLines.join("\n"),
        id: lastEventId,
      });
    }
    eventName = "";
    dataLines = [];
    hasData = false;
  };

  const processLine = (line: string) => {
    if (line === "") {
      dispatch();
      return;
    }
    if (line.startsWith(":")) {
      options.onComment?.(line.slice(1).replace(/^ /, ""));
      return;
    }
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);

    switch (field) {
      case "event":
        eventName = value;
        break;
      case "data":
        dataLines.push(value);
        hasData = true;
        break;
      case "id":
        if (!value.includes("\u0000")) lastEventId = value;
        break;
      case "retry":
        if (/^\d+$/.test(value)) options.onRetry?.(Number(value));
        break;
      default:
        break;
    }
  };

  const push = (chunk: string) => {
    if (!chunk) return;
    let text = chunk;
    if (skipLeadingLf) {
      skipLeadingLf = false;
      if (text.startsWith("\n")) text = text.slice(1);
    }
    buffer += text;

    let start = 0;
    for (let i = 0; i < buffer.length; i++) {
      const char = buffer[i];
      if (char !== "\n" && char !== "\r") continue;
      processLine(buffer.slice(start, i));
      if (char === "\r") {
        if (i + 1 < buffer.length) {
          if (buffer[i + 1] === "\n") i++;
        } else {
          skipLeadingLf = true;
        }
      }
      start = i + 1;
    }
    buffer = buffer.slice(start);
  };

  const reset = () => {
    buffer = "";
    skipLeadingLf = false;
    eventName = "";
    dataLines = [];
    hasData = false;
  };

  const flush = () => {
    if (buffer.length > 0 && buffer.startsWith(":")) processLine(buffer);
    reset();
  };

  return { push, flush, reset };
}
